'use client';

/**
 * PhotoLightbox — fullscreen photo view used everywhere a single image
 * needs the "tap to enlarge" treatment: project main photo, finish
 * photos, technique photos, inspiration board, yarn detail.
 *
 * Recipe (canonical, identical across the 6 inline copies it replaces):
 *   - fixed inset-0, z-[100] (above any open Modal at z-[60])
 *   - bg-black/90 (semi-opaque inky backdrop, lets the page hint through)
 *   - centered BlobImage, max-w/max-h:100%, object-contain (preserves ratio)
 *   - tap anywhere on backdrop dismisses
 *   - top-right "✕" button: 40×40, rounded-full, white/20 glass
 *
 * Critical iOS PWA correction (Veronika 2026-05-18 Telegram msg 3934):
 * the close button needs `safe-area-inset-top` padding so it doesn't
 * sit under the iPhone status bar (battery / wifi icons). The inline
 * sites all used `top-4` (16px) which is below the ~47px status-bar
 * area on iPhones with a Dynamic Island, making the button untappable.
 * This primitive uses `top: max(env(safe-area-inset-top), 16px) + 4px`
 * so non-PWA browsers still get the original 20px-ish offset while iOS
 * PWA users land below the status bar reliably. Same for right inset
 * in landscape with notch.
 *
 * Use anywhere a photo needs full-view treatment:
 *   {viewPhoto && (
 *     <PhotoLightbox src={viewPhoto} onClose={() => setViewPhoto(null)} />
 *   )}
 *
 * The `closeLabel` prop accepts an i18n string; defaults to "Close".
 * The `aria-label` of the backdrop dialog comes from `label` (defaults
 * to "Photo").
 */

import { X } from 'lucide-react';
import BlobImage from '@/components/BlobImage';

export type PhotoLightboxProps = {
  /** Photo source — a `blob:sha256-...` ref or any URL BlobImage accepts. */
  src: string;
  /** Called when the user dismisses (backdrop tap or close button). */
  onClose: () => void;
  /** Optional a11y label for the dialog (defaults to "Photo"). */
  label?: string;
  /** Optional a11y label for the close button (defaults to "Close"). */
  closeLabel?: string;
};

export function PhotoLightbox({
  src,
  onClose,
  label = 'Photo',
  closeLabel = 'Close',
}: PhotoLightboxProps) {
  return (
    <div
      role="dialog"
      aria-label={label}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm cursor-zoom-out p-4"
      onClick={onClose}
    >
      <BlobImage src={src} className="max-w-full max-h-full object-contain" />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={closeLabel}
        data-testid="photo-lightbox-close"
        className="absolute w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        style={{
          top: 'max(env(safe-area-inset-top), 16px)',
          right: 'max(env(safe-area-inset-right), 16px)',
        }}
      >
        <X size={20} />
      </button>
    </div>
  );
}

export default PhotoLightbox;
